/* globals window, document */

import Vue from 'vue';
import VueMq from 'vue-mq';
import VueYandexMetrika from 'vue-yandex-metrika';
import VueAnalytics from 'vue-analytics';
import vClickOutside from 'v-click-outside';
import { isPast, subMinutes } from 'date-fns';
import 'es6-promise/auto';
import { createApp } from './app';
import EventVisitHistoryRepo from '@/providers/EventVisitHistoryRepo.js';
import ProgressBar from '@/components/ext/ProgressBar.vue';
import createCookies from '@/tools/cookie';
import createSession from '@/tools/session';
import config from '@/config';

import bvi from '../public/bvi/js/bvi';

Vue.use(vClickOutside);

Vue.use(VueMq, {
  breakpoints: {
    mobile: 640,
    phablet: 768,
    tablet: 1024,
    desktop: Infinity,
  },
});

const cookie = createCookies();
const session = createSession();

// ключ для локал сторидж
const { authlocalStorageKey } = config;

// import {MediaQueries} from 'vue-media-queries';
// const mediaQueries = new MediaQueries();
// Vue.use(mediaQueries);

// Глобальный progress-bar
const bar = (Vue.prototype.$bar = new Vue(ProgressBar).$mount());
if (document) document.body.append(bar.$el);

// Заведем заранее переменные
let providers, store, app, router;

// Нестрогое сравнение объектов
const isObjectEqual = (a, b) => {
  const c = Object.keys(Object.assign({}, a, b));
  return c.map((k) => a[k] == b[k]).filter((i) => !!i).length === c.length;
};

// Глобальная примесь для проверки изменения параметров компонента (asyncRouteData)
Vue.mixin({
  beforeRouteUpdate(route, from, next) {
    const isEqual =
      route.name === from.name &&
      isObjectEqual(route.params, from.params) &&
      isObjectEqual(route.query, from.query);
    const { timeBeforeDieExpiredAt } = config;
    const options = this.$options || {};
    const { storeName, storeRegister } = options;
    const { asyncData } = this.asyncData !== undefined ? this : options;

    if (asyncData && !isEqual && !store.state.loading) {
      bar.start();
      if (storeName && storeRegister) {
        app.registerModule(storeName, storeRegister, store, providers);
      }

      asyncData({ store, route, providers })
        .then(() => {
          bar.finish();
          store.commit('setLoading', true);
          next();
        })
        .catch(() => {
          bar.fail();
          store.commit('setLoading', false);
          next();
        });
    } else {
      next();
    }
  },
});

// Дерегистрация локальных хранилищ
Vue.mixin({
  destroyed() {
    if (this.$options.storeName) {
      this.$root.unregisterModule(this.$options.storeName);
    }
  },
});

const eventVisitHistory = new EventVisitHistoryRepo(
  config.eventVisitHistory.localStorageKey,
  config.eventVisitHistory.count,
);

// Создадим приложение и сопутствующие объекты
const created = createApp({
  cookie,
  session,
  eventVisitHistory,
  captureError: null,
  isClient: true,
});
app = created.app;
providers = created.providers;
store = created.store;
router = created.router;

/**
 * Первичная инициализация хранилища
 * Сосотояние определяется во время запроса к серверу и передается в браузер
 */
if (window.__INITIAL_STATE__) {
  store.replaceState(window.__INITIAL_STATE__);
} else {
  store.dispatch('init');
}

// Если токен есть, меняем значение в стор.
store.commit(
  'setIsTokensExist',
  !!session.load(authlocalStorageKey) ? true : false,
);

const { metrikaId, googleId } = store.state.regionProfile;

if (metrikaId) {
  Vue.use(VueYandexMetrika, {
    router,
    id: metrikaId,
    env: process.env.NODE_ENV,
    skipSamePath: false,
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
    },
  });
}

if (googleId) {
  Vue.use(VueAnalytics, {
    router,
    id: googleId,
    autoTracking: {
      // screenview: true,
    },
  });
}

// Ждем, пока  роутер получит все асинхронные данные перед хуками (для асинхронных компонентов)
router.onReady(() => {
  const matchedComponents = router.getMatchedComponents();
  matchedComponents.forEach((item) => {
    const { options } = item;
    const { storeName, storeRegister } = options || item;
    if (storeName && storeRegister)
      app.registerModule(storeName, storeRegister, store, providers, true);
  });

  /**
   * Accessibility mode
   */
  window.onAccessibilityEnableEvent = new CustomEvent("accessibility.on", {
    detail: {
      mode: 'on'
    }
  });
  window.onAccessibilityToggleEvent = new CustomEvent("accessibility.toggle", {
    detail: {
      mode: 'toggle'
    }
  });

  window.addEventListener("accessibility.on", function(event) {
    // here is the Vue code
    let triggerButton = document.querySelector('.js-bvi-open')

    var handler = function () {
      window.dispatchEvent(window.onAccessibilityToggleEvent);
      console.log(handler);
      this.removeEventListener('click', handler);
    }

    if (!triggerButton) {
      triggerButton = document.createElement('div');
      triggerButton.classList.add('js-bvi-open')
      triggerButton.style = 'display: none;';
      document.body.prepend(triggerButton);
      new isvek.Bvi({
        target: '.js-bvi-open',
        fontSize: 16,
      });
      triggerButton.click();
      let bviController = document.querySelector('[data-bvi="close"]');
      bviController.addEventListener('click', handler)
      window.dispatchEvent(window.onAccessibilityToggleEvent);
    } else {
      triggerButton.click();
      let bviController = document.querySelector('[data-bvi="close"]');
      bviController.addEventListener('click', handler)
      window.dispatchEvent(window.onAccessibilityToggleEvent);
    }
  });

  /**
   * Хук роутера для обработки asyncData.
   * Делаем это после инициализации роутера, чтобы избежать дублирования
   * асинхронным запросов к данным, котрые мы уже получили.
   */
  router.beforeResolve((to, from, next) => {
    let diffed = false;

    const route = to;
    const matched = router.getMatchedComponents(route);
    const prevMatched = router.getMatchedComponents(from);
    const activated = matched.filter((c, i) => {
      return diffed || (diffed = prevMatched[i] !== c);
    });

    if (!activated.length) return next();

    bar.start();
    Promise.all(app.registerAsyncData(activated, store, route, providers))
      .then(() => {
        bar.finish();
        next();
      })
      .catch((err) => {
        bar.fail();
        if (err.code === 301 || err.code === 302) {
          window.location.href = err.data;
        } else {
          next();
        }
      });
  });

  // Монитируемся в DOM
  app.$mount('#app');


});



// WEBPACK FOOTER //
// ./src/entry-client.js